import {AbstractPayment, OvereasProcessor} from "./OvereasProcessor";
import {WebHelpers} from "./WebHelpers";
import {AbstractModule} from "./AbstractModule";
import {Order} from "./structs/Order";

interface TrackinInfo {
    g: string; // the chosen gateway for this user
}

/**
 * Class to interact with the Woocommerce store of a WordPress installation.
 */
export class Woocommerce extends AbstractModule {
    protected static readonly COOKIE_NAME = "ospay";
    protected static readonly COOKIE_LIFETIME_DAYS = 365;

    protected checkServerPaymentTimerID: number = 0;
    protected shouldReloadPage: boolean = false; // prevent reloading on order pages where it was already paid on page load
    protected originalTooltipText: string = "";

    constructor(plugin: OvereasProcessor, webHelpers: WebHelpers) {
        super(plugin, webHelpers);
        this.plugin.$(this.plugin.window.document).ready(($) => {
            // #overs-payment-frame is now dynamic
            if (this.plugin.$(".overs-prepay-msg, #overs-payment-button, #payment.woocommerce-checkout-payment").length !== 0) {
                //this.addButtonEvents();
                this.ensureCookiePresence();
                this.addPaymentTacking();
            }
            this.addCopyLinkEvent();

            setTimeout(() => {
                const config = this.plugin.getConfig();
                if (config.checkPaymentIntervalSec > 0)
                    setTimeout(this.checkPaymentStatus.bind(this, true, true),config.checkPaymentIntervalSec * 1000);
            }, 1000);
        });
    }

    // ################################################################
    // ###################### PRIVATE FUNCTIONS #######################

    /*
    protected addButtonEvents() {
        this.plugin.$("#overs-payment-button").on("click", (event) => {
            console.log("pay")
            this.plugin.window.open()
        });
    }
     */

    /**
     * Adds a cookie to remember the customer's payment option.
     */
    protected addPaymentTacking() {
        if (typeof this.plugin.window.btoa !== "function" || this.plugin.getConfig().gateway === undefined)
            return;
        let data: TrackinInfo = {
            g: this.plugin.getConfig().gateway
        }
        const dataEncoded = this.webHelpers.serializeBase64Data(data);
        this.webHelpers.setCookie(Woocommerce.COOKIE_NAME, dataEncoded, Woocommerce.COOKIE_LIFETIME_DAYS);
        this.plugin.window.localStorage.setItem(Woocommerce.COOKIE_NAME, dataEncoded);
    }

    protected readTrackingInfo(onlyCookie: boolean = false): TrackinInfo {
        if (typeof this.plugin.window.btoa !== "function")
            return null;
        let cookie = this.webHelpers.getCookie(Woocommerce.COOKIE_NAME);
        if (cookie) {
            let cookieUnserialized = this.webHelpers.unserializeBase64Data(cookie);
            if (cookieUnserialized)
                return cookieUnserialized;
        }
        if (onlyCookie === true)
            return null;
        let localStorageData = this.plugin.window.localStorage.getItem(Woocommerce.COOKIE_NAME);
        if (localStorageData) {
            let localStorageUnserialized = this.webHelpers.unserializeBase64Data(localStorageData);
            if (localStorageUnserialized)
                return localStorageUnserialized;
        }
        return null;
    }

    protected ensureCookiePresence() {
        // our backend only reads the cookie on Ajax requests
        // restore previous data from localStorage
        let info = this.readTrackingInfo();
        if (!info || !info.g)
            return;
        let infoCookie = this.readTrackingInfo(true);
        if (this.plugin.getConfig().gateway !== undefined && infoCookie.g === this.plugin.getConfig().gateway)
            return;
        let data: TrackinInfo = {
            g: info.g
        }
        const dataEncoded = this.webHelpers.serializeBase64Data(data);
        this.webHelpers.setCookie(Woocommerce.COOKIE_NAME, dataEncoded, Woocommerce.COOKIE_LIFETIME_DAYS);
    }

    /**
     * Check the payment status
     * @param repeat Whether to repeat the check periodically until the amount is fully paid.
     * @param forceReCheck Force an immediate re-check from our server to ask the Blockchain backend
     * @param checkAgainIfNotFullyPaid Query WP again if the payment hasn't been fully paid (but BCHD says so)
     */
    protected checkPaymentStatus(repeat = true, forceReCheck = false, checkAgainIfNotFullyPaid = false) {
        const config = this.plugin.getConfig();
        if (config.nonce === undefined || config.orderID <= 0)
            return;
        let params: any = {
            n: config.nonce,
            oid: config.orderID
        }
        if (forceReCheck === true)
            params.f = "1";
        clearTimeout(this.checkServerPaymentTimerID);
        this.webHelpers.getApi("/wp-json/overseas-processor/v1/order-status", params, (data) => {
            // TODO continue querying if we get sth other than JSON?
            if (repeat === true && config.checkPaymentIntervalSec > 0) { // TODO abort checking after x minutes?
                clearTimeout(this.checkServerPaymentTimerID); // ensure we don't have multiple timers running
                this.checkServerPaymentTimerID = this.plugin.window.setTimeout(this.checkPaymentStatus.bind(this, true, forceReCheck), config.checkPaymentIntervalSec * 1000); // always repeat the check even if there was an error
            }
            if (data.error === true) {
                this.plugin.window.console.error("Error checking payment status: %s", data.errorMsg);
                return;
            }
            if (data.data && data.data.length >= 1) {
                const order: Order = Object.assign(new Order(), data.data[0]);
                if (order.status === "completed")
                    return; // user opened order page after shipping

                if (this.shouldReloadPage === false)
                    this.shouldReloadPage = order.status !== "paid";
                if (order.status === "paid") {
                    if (this.shouldReloadPage === true)
                        this.plugin.window.location.reload();
                }
                if (order.status === "suspended" || this.shouldReloadPage === false) {
                    clearTimeout(this.checkServerPaymentTimerID);
                }
            }
        }).fail((err) => {
            this.webHelpers.log(["Error querying WP for payment status", err]);
            clearTimeout(this.checkServerPaymentTimerID);
            this.checkServerPaymentTimerID = this.plugin.window.setTimeout(this.checkPaymentStatus.bind(this, true, forceReCheck), config.checkPaymentIntervalSec * 1000);
        });
    }

    protected addCopyLinkEvent() {
        /**
         * <div class="overs-tooltip">
         *     <a target="_blank" href="{order_link}">this payment page</a>
         *     <span class="overs-tooltiptext">Copy to clipboard</span>
         * </div>
         */
        this.plugin.$(".overs-browser-msg a").on("click", (event) => {
            event.preventDefault();
            this.copyLink(event.target);
            return false; // not needed
        }).on("mouseout", (event) => {
            this.onMouseOutFunc(event.target);
        });
    }

    protected copyLink(copyText: HTMLElement) {
        //var copyText = document.getElementById("myInput");
        // for input button
        //copyText.select();
        //copyText.setSelectionRange(0, 99999);
        //let copyValue = copyText.value;
        let copyValue = this.plugin.$(copyText).attr("href");
        navigator.clipboard.writeText(copyValue);

        //let tooltip = document.getElementById("myTooltip");
        //tooltip.innerHTML = "Copied: " + copyValue;
        const config = this.plugin.getConfig();
        let diplayTxt = copyValue.length > 20 ? config.tr.copied : config.tr.copied + ": " + copyValue;
        this.originalTooltipText = this.plugin.$(".overs-tooltiptext").html();
        this.plugin.$(".overs-tooltiptext").html(diplayTxt);
    }

    protected onMouseOutFunc(copyText: HTMLElement) {
        //let tooltip = document.getElementById("myTooltip");
        //tooltip.innerHTML = "Copy to clipboard";
        this.plugin.$(".overs-tooltiptext").html(this.originalTooltipText);
    }
}
